import {
    Flex,
} from '@gmg/gmg-react-components';
import React, { FC, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import OrganizationBreadCrumb from 'src/components/shared/OrganizationBreadCrumb';
import { ViewInfo } from 'src/components/shared/BreadCrumbItem';
import { isGmgAdminRole, isGmgPartnerRole } from 'src/role';
import { hasUserPermissionToViewParentOrganization } from 'src/shared/permissions';
import WrappedSpinner from 'src/shared/WrappedSpinner';
import { Organization } from 'src/viewModels/Organization';
import styled from 'styled-components';
import AppContext, { Role } from '../AppContext';
import { useGetOrganization } from '../graphql/useGetOrganization';

interface OrganizationContainerProps {
    checkBox?: JSX.Element;
    searchBar?: JSX.Element;
    button?: JSX.Element;
    viewInfo: ViewInfo;
    children: React.ReactNode;
    onOrganizationLoad: (org: Organization, maintainableRoles: Array<Role>) => void;
}

const OrganizationContainer: FC<OrganizationContainerProps> = (props) => {
    const { t } = useTranslation();
    const { orgId } = useParams();
    const context = useContext(AppContext);
    const { data, error, isLoading } = useGetOrganization(orgId!);

    useEffect(() => {
        if (!data) return;

        props.onOrganizationLoad(data.organization, data.maintainableRoles);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (!error) {
            return;
        }
        context.onError(error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);

    const hasGmgAdminPermission = isGmgAdminRole(context.user?.role);
    const hasOrganizationGroupAdminPermission = hasUserPermissionToViewParentOrganization({
        userRole: context.user?.role,
        userOrgId: context.user?.orgId,
        parentOrganizationId: data?.organization.parentOrgData?.id,
    });
    const showParentOrganization = !!(
        data?.organization.parentOrgData?.name &&
        data?.organization.parentOrgData?.id &&
        hasOrganizationGroupAdminPermission
    );

    const isPartnerPortalNavigationVisible = context.featureFlags.showPartnerPortal && isGmgPartnerRole(context.user!.role);
    const navBarHeight = isPartnerPortalNavigationVisible ? 'var(--navBarHeightWithDealerPortal)' : 'var(--navBarHeightWithoutDealerPortal)';

    return (

        <Flex flexDirection="column" gap="2px" bg="background">
            {/* Header section */}
            <StyledFlex>
                <Flex ml="22px" justifyContent="center" alignItems="center" gap="16px">
                    <OrganizationBreadCrumb
                        t={t}
                        hasGmgAdminPermission={hasGmgAdminPermission}
                        organizationName={data?.organization.name}
                        showParentOrganization={showParentOrganization}
                        parentOrganizationName={data?.organization.parentOrgData?.name}
                        parentOrganizationId={data?.organization.parentOrgId}
                        viewInfo={props.viewInfo}
                    />
                </Flex>
                <Flex justifyContent="center" alignItems="center" gap="16px">
                    <div style={{ overflow: 'hidden' }}>
                        {props.checkBox || null}
                    </div>
                    {props.searchBar}
                    <ButtonWrapper>
                        {props.button || null}
                    </ButtonWrapper>
                </Flex>
            </StyledFlex>

            {/* Main section */}
            <Flex flexGrow={1} height="var(--app-content-height)" minHeight={navBarHeight} overflow="hidden" justifyContent="center" bg="backgroundPaper">
                {isLoading
                    ? <WrappedSpinner label={t('Common.Spinner', 'Loading...')} labelPosition="bottom" />
                    : props.children
                }
            </Flex>
        </Flex>
    );
};

const ButtonWrapper = styled.div`
    flex-shrink: 1;
    display: flex;
    align-items: center;
    flex-direction: row;
    gap: 8px;
`;

const StyledFlex = styled(Flex)`
    width: 100%;
    height: 64px;
    justify-content: space-between;
    align-items: center;
    text-align: center;
    background: ${({ theme }) => theme.colors.backgroundPaper};
    padding-right: 24px;
`;

export default OrganizationContainer;
